/* eslint-disable */
import React, { useState, useEffect, useCallback, useRef } from "react";
import LandingPage from "./LandingPage";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsOfService from "./TermsOfService";
import LoginPage from "./LoginPage";
import toast, { Toaster } from "react-hot-toast";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import "./App.css";
import FindTab from "./components/FindTab";
import PasteTab from "./components/PasteTab";
import PipelineTab from "./components/PipelineTab";
import OutreachTab from "./components/OutreachTab";
import Header from "./components/Header";
import StatsBar from "./components/StatsBar";
import { API, CITY_MAP, EMPTY_LEAD_META, GROQ_KEY, HUNTER_KEY, OPPORTUNITY_COUNTRY_OPTIONS, PIPELINE_PAGE_SIZE, SOURCES, calcScore, groqChat, hasUsableWebsite, inferCountryFromOpportunityItem, inferWebsiteFromOpportunityItem, localOutreach, localQualify } from "./appShared";
import { autoPingWebsite, enrichLeadDirectContact, filterCommercialLeads, searchDenmark, searchDoffin, searchMaps, searchNorway, searchPublicOpportunities, searchUK, searchSAM, searchContractsFinder, searchTED } from "./services/leadSearchService";
import { crawlProjectDetails as fetchProjectDetailsData, crawlWebsiteEmail, generateOutreachDraft, lookupHunterEmail, qualifyLeadWithAI, sendGmailMessage } from "./services/leadOpsService";
import { usePipelineData } from "./hooks/usePipelineData";
import { useIdleLogout } from "./hooks/useIdleLogout";
import { processBatchWithProgress } from "./utils/batchProcessor";
import { FaBell, FaBullseye, FaChartBar, FaMagnifyingGlass, FaPaste } from "react-icons/fa6";
import { FaFileCsv, FaRightFromBracket, FaEllipsisVertical, FaTrash } from "react-icons/fa6";

//  Main App 
export default function App() {
  // WAVEPROSPECT OAUTH CALLBACK
  const [legalPage, setLegalPage] = useState(null);
  const [authToken, setAuthToken] = useState(() => localStorage.getItem("lh_token") || "");
  const [currentUser, setCurrentUser] = useState(null);


  const [showLanding, setShowLanding] = useState(() => {
    if (localStorage.getItem("lh_token")) return false;
    return localStorage.getItem("lh_launched") !== "true";
  });
  const [tab, setTab]           = useState(() => {
    const saved = localStorage.getItem("lh_tab");
    return ["find", "paste", "pipeline"].includes(saved || "") ? saved : "find";
  });
  const [selected, setSelected] = useState(null);

  const [confirmModal, setConfirmModal] = useState(null);

  // Find Leads state
  const [sources, setSources]   = useState([]);
  const [industry, setIndustry] = useState("All Industries");
  const [city, setCity]         = useState("All Cities");
  const [keyword, setKeyword]   = useState("");
  const [oppCountries, setOppCountries] = useState([]);
  const [oppLimit, setOppLimit] = useState(150);
  const [oppRefineCountries, setOppRefineCountries] = useState(false);
  const [searching, setSearching] = useState(false);
  const globalLoading = searching;
  const [streamingCount, setStreamingCount] = useState(0);
  const [searchStatus, setSearchStatus] = useState("");
  const [searchError, setSearchError]   = useState("");
  const [dbSaveProgress, setDbSaveProgress] = useState({ saved: 0, attempted: 0, skipped: 0, failed: 0 });
  const [leadNotifications, setLeadNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const notificationsRef = useRef(null);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const moreMenuRef = useRef(null);
  const marketSeenKeysRef = useRef(new Set());
  const marketWatchInitializedRef = useRef(false);
  const marketWatchBusyRef = useRef(false);
  const unreadNotificationCount = leadNotifications.reduce((count, item) => count + (item.read ? 0 : 1), 0);
  const dbSaveStatus = (dbSaveProgress.attempted > 0 || dbSaveProgress.failed > 0)
    ? `DB save: ${dbSaveProgress.saved}/${dbSaveProgress.attempted} saved` +
      (dbSaveProgress.skipped > 0 ? `, ${dbSaveProgress.skipped} skipped` : "") +
      (dbSaveProgress.failed > 0 ? `, ${dbSaveProgress.failed} failed` : "")
    : "";

  const {
    leads,
    setLeads,
    leadMeta,
    pipeFilter,
    setPipeFilter,
    pipeSource,
    setPipeSource,
    pipeSearch,
    setPipeSearch,
    pipeQuickFilter,
    setPipeQuickFilter,
    viewMode,
    setViewMode,
    detailTab,
    setDetailTab,
    pipelineTotal,
    pipelineHasMore,
    pipelineLoading,
    loadLeadMeta,
    loadMorePipeline,
    refreshPipeline,
    saveLeads,
    updateLead,
    deleteLead,
    clearAllLeads,
  } = usePipelineData({
    api: API,
    emptyLeadMeta: EMPTY_LEAD_META,
    pageSize: PIPELINE_PAGE_SIZE,
    calcScore,
    selected,
    setSelected,
  });

  // Gmail status
  const [gmailStatus, setGmailStatus] = useState({ connected: false, email: "" });


  // Fetch current user profile whenever authToken changes (in-memory only)
  useEffect(() => {
    if (!authToken) {
      setCurrentUser(null);
      return;
    }
    fetch(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${authToken}` } })
      .then((r) => r.json())
      .then((d) => {
        if (d.ok) setCurrentUser(d.user);
      })
      .catch((e) => console.error("Auth/me error:", e));
  }, [authToken]);

  // Handle Google OAuth callback (persisted to localStorage so refresh keeps session)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authStatus = params.get("auth");
    const token = params.get("token");
    const email = params.get("email");

    if (authStatus === "success" && token) {
      setAuthToken(token);
      localStorage.setItem("lh_token", token);
      setShowLanding(false);
    }

    if (authStatus || token || email) {
      const cleanUrl = `${window.location.origin}${window.location.pathname}`;
      window.history.replaceState({}, document.title, cleanUrl);
    }
  }, []);


  // Check Gmail connection on load
  useEffect(() => {
    fetch(`${API}/api/gmail/status`)
      .then(r => r.json())
      .then(d => setGmailStatus(d))
      .catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.setItem("lh_tab", tab);
  }, [tab]);

  useEffect(() => {
    if (!notificationsOpen) return;
    const onPointerDown = (event) => {
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [notificationsOpen]);

  useEffect(() => {
    if (!moreMenuOpen) return;
    const onPointerDown = (event) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target)) {
        setMoreMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [moreMenuOpen]);

  const pushLeadNotification = useCallback((payload) => {
    setLeadNotifications((prev) => [{
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      createdAt: Date.now(),
      read: false,
      ...payload,
    }, ...prev].slice(0, 50));
  }, []);

  const toMarketKey = useCallback((item) => {
    const sourceUrl = String(item?.sourceUrl || item?.link || "").trim().toLowerCase();
    const name = String(item?.name || item?.title || "").trim().toLowerCase();
    const country = String(item?.country || "").trim().toLowerCase();
    return `${sourceUrl}|${name}|${country}`;
  }, []);

  useEffect(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("lh_market_seen") || "[]");
      if (Array.isArray(raw)) {
        marketSeenKeysRef.current = new Set(raw.filter(Boolean));
      }
    } catch {}
  }, []);

  const persistSeenMarketKeys = useCallback(() => {
    try {
      localStorage.setItem("lh_market_seen", JSON.stringify(Array.from(marketSeenKeysRef.current).slice(-800)));
    } catch {}
  }, []);

  const runMarketWatch = useCallback(async () => {
    if (searching || marketWatchBusyRef.current) return;
    marketWatchBusyRef.current = true;
    try {
      const explicitOppCountries = [...new Set((oppCountries || []).filter((value) => value !== "GLOBAL"))];
      const watchCountries = explicitOppCountries.length > 0 ? explicitOppCountries.slice(0, 6) : ["GLOBAL"];
      const watchQuery = (keyword && keyword.trim()) || (industry !== "All Industries" ? industry : "procurement tender");

      const watchedBatches = await Promise.all(
        watchCountries.map((countryId) => searchPublicOpportunities({
          api: API,
          q: watchQuery,
          countryId,
          limit: countryId === "GLOBAL" ? 20 : 10,
          industry: "All Industries",
          city: "All Cities",
          sources: SOURCES,
          inferWebsiteFromOpportunityItem,
          inferCountryFromOpportunityItem,
        }).catch(() => []))
      );

      const merged = watchedBatches.flat();
      if (!Array.isArray(merged) || merged.length === 0) return;

      const uniqueByKey = new Map();
      for (const item of merged) {
        const key = toMarketKey(item);
        if (!key) continue;
        if (!uniqueByKey.has(key)) uniqueByKey.set(key, item);
      }

      const currentItems = Array.from(uniqueByKey.values());
      if (!marketWatchInitializedRef.current) {
        for (const item of currentItems) {
          marketSeenKeysRef.current.add(toMarketKey(item));
        }
        marketWatchInitializedRef.current = true;
        persistSeenMarketKeys();
        return;
      }

      const freshItems = currentItems.filter((item) => !marketSeenKeysRef.current.has(toMarketKey(item)));
      if (freshItems.length === 0) return;

      for (const item of freshItems) {
        marketSeenKeysRef.current.add(toMarketKey(item));
      }
      persistSeenMarketKeys();

      const byCountry = [...new Set(freshItems.map((item) => item.country).filter(Boolean))];
      const countryText = byCountry.length > 0
        ? `${byCountry.slice(0, 3).join(", ")}${byCountry.length > 3 ? ` +${byCountry.length - 3}` : ""}`
        : "Global";

      pushLeadNotification({
        sourceLabel: "Market Watch",
        count: freshItems.length,
        countries: countryText,
        text: `${freshItems.length} new market lead${freshItems.length !== 1 ? "s" : ""} found`,
      });

      toast.success(`Market alert: ${freshItems.length} new lead${freshItems.length !== 1 ? "s" : ""} (${countryText})`);
    } finally {
      marketWatchBusyRef.current = false;
    }
  }, [API, SOURCES, industry, inferCountryFromOpportunityItem, inferWebsiteFromOpportunityItem, keyword, oppCountries, persistSeenMarketKeys, pushLeadNotification, searching, toMarketKey]);

  useEffect(() => {
    const intervalMs = 4 * 60 * 1000;
    runMarketWatch();
    const timerId = setInterval(runMarketWatch, intervalMs);
    return () => clearInterval(timerId);
  }, [runMarketWatch]);

  const toggleNotifications = useCallback(() => {
    setNotificationsOpen((prev) => {
      const next = !prev;
      if (!prev) {
        setLeadNotifications((items) => items.map((item) => ({ ...item, read: true })));
      }
      return next;
    });
  }, []);

  // Send email via Gmail
  const sendEmail = async (lead, subject, body) => {
    if (!lead.email) return toast.error("No email address for this lead");
    try {
      await sendGmailMessage({ api: API, to: lead.email, subject, body, leadId: lead.id });
      await updateLead(lead.id, { status: "Contacted" });
      toast.success(`Email sent to ${lead.email}`);
    } catch (e) {
      toast.error("Failed to send: " + e.message);
    }
  };

  // Paste tab
  const [pasteText, setPasteText]       = useState("");
  const [pasteLoading, setPasteLoading] = useState(false);
  const [pasteSourceUrl, setPasteSourceUrl] = useState("");
  const [pasteSourceType, setPasteSourceType] = useState("LinkedIn Public Post");
  const [pastePostedAt, setPastePostedAt] = useState("");
  const [pasteLegalBasis, setPasteLegalBasis] = useState("Legitimate interest (B2B)");
  // react-hot-toast used directly via toast.success / toast.error

  const signOut = useCallback(() => {
    setAuthToken(null);
    localStorage.removeItem("lh_token");
    setShowLanding(true);
  }, []);

  const { showWarning: showIdleWarning, secondsLeft: idleSecondsLeft, stayLoggedIn } = useIdleLogout({
    active: !!authToken,
    onLogout: signOut,
  });

  //  Search 
  const cities = sources.length === 1 ? (CITY_MAP[sources[0]] || ["All Cities"]) : ["All Cities"];
  const selectedSourceLabels = sources.map(id => SOURCES.find(s => s.id === id)?.label || id);
  const INITIAL_WEBSITE_ENRICH_CAP = 40;
  const INITIAL_DIRECT_ENRICH_CAP = 25;
  const DIRECT_ENRICH_BATCH_SIZE = 8;

  const getDomainKey = (website) => {
    if (!website) return "";
    try {
      const normalized = String(website).startsWith("http") ? String(website) : `https://${website}`;
      return new URL(normalized).hostname.replace(/^www\./i, "").toLowerCase();
    } catch {
      return String(website).trim().toLowerCase();
    }
  };

  useEffect(() => {
    // Keep city filter in sync with selected source to avoid cross-country queries.
    if (sources.length !== 1) {
      if (city !== "All Cities") setCity("All Cities");
      return;
    }
    const allowedCities = CITY_MAP[sources[0]] || ["All Cities"];
    if (!allowedCities.includes(city)) setCity("All Cities");
  }, [sources, city]);

const handleSearch = async () => {
    if (sources.length === 0) return toast.error("Select at least one source");
    setSearching(true);
  setStreamingCount(0);
    setSearchError("");
  setDbSaveProgress({ saved: 0, attempted: 0, skipped: 0, failed: 0 });
  let allResults = [];
  let pipelineSwitched = false;
  const knownIds = new Set(leads.map((lead) => lead.id));
  const incrementallySaved = [];
  const sourceDiagnostics = [];
  let leadMetaRefreshStarted = false;
  const dbStats = { saved: 0, attempted: 0, skipped: 0, failed: 0 };
  const syncDbProgress = () => setDbSaveProgress({ ...dbStats });

    const effectiveSources = [...new Set(sources)];

    for (const srcId of effectiveSources) {
      try {
        const q = [keyword, industry !== "All Industries" ? industry : "", city !== "All Cities" ? city : ""].filter(Boolean).join(" ");
        const sourceLabel = SOURCES.find(s => s.id === srcId)?.label || srcId;
        if (srcId === "OPP") {
          const explicitOppCountries = [...new Set(oppCountries.filter(c => c !== "GLOBAL"))];
          const isOppGlobalMode = !oppRefineCountries || explicitOppCountries.length === 0;
          setSearchStatus(`Searching from ${sourceLabel}${isOppGlobalMode ? " (Global)" : ` (${explicitOppCountries.length} countries)`}`);
        } else {
          setSearchStatus(`Searching from ${sourceLabel}`);
        }
        let results = [];
        if      (srcId === "NO") results = await searchNorway({ api: API, q, industry, city });
        else if (srcId === "GB") results = await searchUK({ api: API, q, city, industry });
        else if (srcId === "DK") results = await searchDenmark({ api: API, q });
        else if (srcId === "DOFFIN") results = await searchDoffin({ api: API, q, keyword });
        else if (srcId === "SAM") results = await searchSAM({ api: API, q, keyword });
        else if (srcId === "CF") results = await searchContractsFinder({ api: API, q, keyword });
        else if (srcId === "TED") results = await searchTED({ api: API, q, keyword });
        else if (srcId === "OPP") {
          const oppQuery = q || (industry !== "All Industries" ? industry : "business services");
          const explicitOppCountries = [...new Set(oppCountries.filter(c => c !== "GLOBAL"))];
          const allOppCountryIds = OPPORTUNITY_COUNTRY_OPTIONS.map(c => c.id).filter(c => c !== "GLOBAL");
          const globalOnlyMode = explicitOppCountries.length === 0;
          const selectedOppCountries = globalOnlyMode
            ? [...allOppCountryIds, "GLOBAL"]
            : [...new Set(["GLOBAL", ...explicitOppCountries])];

          const perCountryLimit = globalOnlyMode
            ? Math.max(12, Math.floor(oppLimit / Math.max(1, allOppCountryIds.length)))
            : oppLimit;

          const batches = await Promise.all(
            selectedOppCountries.map(cc =>
              searchPublicOpportunities({
                api: API,
                q: oppQuery,
                countryId: cc,
                limit: cc === "GLOBAL" ? Math.max(20, perCountryLimit) : perCountryLimit,
                industry,
                city,
                sources: SOURCES,
                inferWebsiteFromOpportunityItem,
                inferCountryFromOpportunityItem,
              })
            )
          );
          const merged = batches.flat();
          const byKey = new Map();
          for (const item of merged) {
            const key = `${(item.sourceUrl || "").toLowerCase()}|${(item.name || "").toLowerCase()}`;
            const existing = byKey.get(key);
            if (!existing) {
              byKey.set(key, item);
              continue;
            }
            // Prefer items with specific country over generic Global.
            if ((existing.country || "").toLowerCase() === "global" && (item.country || "").toLowerCase() !== "global") {
              byKey.set(key, item);
            }
          }
          results = Array.from(byKey.values());
        }
        else {
          const mapsQuery = q
            ? `${q} business ${sourceLabel}`
            : industry !== "All Industries"
              ? `${industry} business ${sourceLabel}`
              : `small business ${sourceLabel}`;
          results = await searchMaps({ api: API, q: mapsQuery, countryId: srcId, sources: SOURCES });
        }

        const rawCount = results.length;
        results = filterCommercialLeads(results);
        const filteredOut = Math.max(0, rawCount - results.length);
        sourceDiagnostics.push({ sourceLabel, rawCount, keptCount: results.length, filteredOut });
        if (filteredOut > 0) {
          setSearchStatus(`${sourceLabel}: fetched ${rawCount}, kept ${results.length}, skipped ${filteredOut}`);
        }

        if (["NO","GB","DK"].includes(srcId) && results.length > 0) {
          const websiteTargets = results.slice(0, INITIAL_WEBSITE_ENRICH_CAP);
          const untouched = results.slice(INITIAL_WEBSITE_ENRICH_CAP);
          setSearchStatus("Finding websites...");
          const enrichedTargets = await processBatchWithProgress(
            websiteTargets,
            (lead) => autoPingWebsite({ api: API, lead }),
            (completed, total) => setSearchStatus(`Finding websites... ${completed}/${total}`),
            DIRECT_ENRICH_BATCH_SIZE
          );
          results = [...enrichedTargets, ...untouched];
        }
        const scored = results.map(l => {
        const normalized = {
          ...l,
          websiteStatus: l.website ? "found" : (l.websiteStatus || "unknown"),
          status: "New",
          decision: "Undecided",
          skills: [],
          note: ""
        };
        return { ...normalized, score: calcScore(normalized) };
      });

        const freshBatch = scored.filter((lead) => !knownIds.has(lead.id));
        if (freshBatch.length > 0) {
          const saveResult = await saveLeads(freshBatch);
          if (!saveResult?.ok) {
            dbStats.attempted += freshBatch.length;
            dbStats.failed += freshBatch.length;
            syncDbProgress();
            toast.error(`Failed to save ${freshBatch.length} leads from ${sourceLabel}`);
            continue;
          }

          const inserted = Number(saveResult.inserted || 0);
          const attempted = freshBatch.length;
          dbStats.attempted += attempted;
          dbStats.saved += inserted;
          dbStats.skipped += Math.max(0, attempted - inserted);
          syncDbProgress();

          const countries = [...new Set(freshBatch.map((lead) => lead.country).filter(Boolean))];
          const countryText = countries.length > 0
            ? `${countries.slice(0, 3).join(", ")}${countries.length > 3 ? ` +${countries.length - 3}` : ""}`
            : "Unknown country";

          freshBatch.forEach((lead) => knownIds.add(lead.id));
          allResults = [...allResults, ...freshBatch];
          incrementallySaved.push(...freshBatch);
          setStreamingCount((prev) => prev + freshBatch.length);

          pushLeadNotification({
            sourceLabel,
            count: freshBatch.length,
            countries: countryText,
            text: `${freshBatch.length} new lead${freshBatch.length !== 1 ? "s" : ""} from ${sourceLabel} (${inserted}/${attempted} saved)`,
          });
          toast.success(`+${freshBatch.length} new lead${freshBatch.length !== 1 ? "s" : ""} from ${sourceLabel} (${countryText}) • DB ${inserted}/${attempted}`);

          if (!leadMetaRefreshStarted) {
            leadMetaRefreshStarted = true;
            loadLeadMeta();
          }
          setLeads((prev) => {
            const existing = new Set(prev.map((lead) => lead.id));
            return [...prev, ...freshBatch.filter((lead) => !existing.has(lead.id))];
          });

          if (!pipelineSwitched) {
            setTab("pipeline");
            pipelineSwitched = true;
          }

          setSearchStatus(`Loaded ${allResults.length} leads so far...`);
        }
      } catch (e) {
        setSearchError(`Search failed: ${SOURCES.find(s => s.id === srcId)?.label}  ${e.message}`);
      }
    }

    if (incrementallySaved.length > 0) {
      const fresh = [...incrementallySaved].sort((a, b) => (a.country || "").localeCompare(b.country || ""));

      const hasDirectContact = (lead) => !!(
        lead.email ||
        lead.phone ||
        lead.projectContactEmails?.[0] ||
        lead.projectContactPhones?.[0]
      );

      let enrichedFresh = fresh;
      let deferredDirectLookupCount = 0;
      const enrichCandidates = fresh.filter(lead => !hasDirectContact(lead) && hasUsableWebsite(lead));
      const enrichTargets = [];
      const seenDomains = new Set();
      for (const lead of enrichCandidates) {
        const domainKey = getDomainKey(lead.website) || lead.id;
        if (seenDomains.has(domainKey)) continue;
        seenDomains.add(domainKey);
        enrichTargets.push(lead);
        if (enrichTargets.length >= INITIAL_DIRECT_ENRICH_CAP) break;
      }
      deferredDirectLookupCount = Math.max(0, enrichCandidates.length - enrichTargets.length);

      if (enrichTargets.length > 0) {
        setSearchStatus("Finding direct contacts...");
        const enrichedList = await processBatchWithProgress(
          enrichTargets,
          (lead) => enrichLeadDirectContact({ api: API, lead, hasUsableWebsite, hunterKey: HUNTER_KEY }),
          (completed, total) => {
            setSearchStatus(`Finding direct contacts... ${completed}/${total}`);
          },
          DIRECT_ENRICH_BATCH_SIZE
        );
        const enrichedMap = new Map(enrichedList.map(lead => [lead.id, lead]));
        enrichedFresh = fresh.map(lead => enrichedMap.get(lead.id) || lead);

        const changed = enrichedFresh.filter((lead) => {
          const prev = incrementallySaved.find((item) => item.id === lead.id);
          if (!prev) return false;
          return (
            lead.email !== prev.email ||
            lead.phone !== prev.phone ||
            (lead.projectContactEmails?.[0] || "") !== (prev.projectContactEmails?.[0] || "") ||
            (lead.projectContactPhones?.[0] || "") !== (prev.projectContactPhones?.[0] || "")
          );
        });

        if (changed.length > 0) {
          await saveLeads(changed);
          setLeads((prev) => {
            const byId = new Map(prev.map((lead) => [lead.id, lead]));
            for (const lead of changed) byId.set(lead.id, { ...byId.get(lead.id), ...lead });
            return Array.from(byId.values());
          });
        }
      }

      const pipelineReady = enrichedFresh;
      const directReadyCount = enrichedFresh.filter(hasDirectContact).length;

      await loadLeadMeta();
      if (sourceDiagnostics.length > 0) {
        const summaryText = sourceDiagnostics
          .map(({ sourceLabel, rawCount, keptCount, filteredOut }) => `${sourceLabel}: ${keptCount}/${rawCount}${filteredOut > 0 ? ` kept, ${filteredOut} skipped` : " kept"}`)
          .join(" | ");
        toast(summaryText, { duration: 5000, icon: "ℹ️" });
      }
      toast.success(
        ` Found ${allResults.length} leads  ${pipelineReady.length} added to pipeline` +
        (directReadyCount > 0 ? `  ${directReadyCount} with email/phone` : "") +
        (deferredDirectLookupCount > 0 ? `  ${deferredDirectLookupCount} deferred` : "") +
        `  DB ${dbStats.saved}/${dbStats.attempted} saved`
      );
    }
    setSearching(false);
    setStreamingCount(0);
    setSearchStatus("");
  };

  //  Paste extract 
  const handlePaste = async () => {
    if (!pasteText.trim()) return;
    setPasteLoading(true);
    try {
      const text = await groqChat(
        `Extract B2B leads from text. Return ONLY a JSON array. Each: {name,city,country,industry,employees,website,phone,email,linkedin}. If field missing use null.`,
        pasteText
      );
      const clean = text.replace(/```json|```/g, "").trim();
      const arr = JSON.parse(clean);
      const normalizedSourceUrl = pasteSourceUrl.trim();
      const newLeads = arr.map((e, i) => ({
        id: `PASTE-${Date.now()}-${i}`,
        ...e,
        websiteStatus: e.website ? "found" : "unknown",
        source: "Paste",
        sourceType: pasteSourceType,
        sourceUrl: normalizedSourceUrl || null,
        postedAt: pastePostedAt || null,
        legalBasis: pasteLegalBasis,
        capturedAt: new Date().toISOString(),
        status: "New",
        decision: "Undecided",
        skills: [],
        note: "",
        score: calcScore({ ...e, websiteStatus: e.website ? "found" : "unknown" }),
      }));
      await saveLeads(newLeads);
      await loadLeadMeta();
      await refreshPipeline();
      setPasteText("");
      setPasteSourceUrl("");
      setPastePostedAt("");
      toast.success(`Extracted ${newLeads.length} leads`);
      setTab("pipeline");
    } catch { toast.error("Failed to extract leads"); }
    setPasteLoading(false);
  };

  //  Pipeline filter 
  const uniqueSources = leadMeta.uniqueSources || [];
  const goToPipeline = (qf) => { setTab('pipeline'); setPipeQuickFilter(qf); };
  const stats = leadMeta;

  //  CSV Export 
  const exportCSV = async () => {
    let exportLeads = leads;
    try {
      const res = await fetch(`${API}/api/leads`);
      const data = await res.json();
      if (res.ok && data.ok && Array.isArray(data.leads)) exportLeads = data.leads;
    } catch {}
    const rows = [["Name","Country","City","Industry","Website","Phone","Score","Status","Decision","Note"]];
    exportLeads.forEach(l => rows.push([l.name,l.country,l.city,l.industry,l.website||"",l.phone||"",l.score,l.status,l.decision,l.note||""]));
    const csv = rows.map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(",")).join("\n");
    const a = document.createElement("a"); a.href = "data:text/csv," + encodeURIComponent(csv);
    a.download = "waveprospect-leads.csv"; a.click();
  };

  //  Hunter email 
  const findEmail = async (lead) => {
    if (lead.websiteStatus === "missing") return toast.error("No website - cannot search domain");
    try {
      const email = await lookupHunterEmail({ api: API, website: lead.website });
      if (email) {
        await updateLead(lead.id, { email });
        toast.success(`Found: ${email}`);
      } else {
        toast.error("No emails found for this domain");
      }
    } catch (e) {
      toast.error("Hunter search failed: " + (e.message || "unknown error"));
    }
  };

  //  Crawl website for emails 
  const crawlEmail = async (lead) => {
    if (!lead.website) return toast.error("No website to crawl");
    toast.loading("Crawling website for emails...", { id: "crawl" });
    try {
      const email = await crawlWebsiteEmail({ api: API, website: lead.website });
      if (email) {
        await updateLead(lead.id, { email });
        toast.success(`Found: ${email}`, { id: "crawl" });
      } else {
        toast.error("No emails found on website", { id: "crawl" });
      }
    } catch (e) {
      toast.error("Crawl failed: " + e.message, { id: "crawl" });
    }
  };

  //  Crawl website for project details 
  const crawlProjectDetails = async (lead) => {
    if (!lead.website) return toast.error("No website to crawl");
    toast.loading("Crawling website for project details...", { id: "crawl-project" });
    try {
      const data = await fetchProjectDetailsData({ api: API, website: lead.website });

      await updateLead(lead.id, {
        projectCompanyName: data.companyName || "",
        projectSummary: data.summary || "",
        projectServices: data.services || [],
        projectSignals: data.projectSignals || [],
        projectPages: data.pages || [],
        projectPagesCrawled: data.pagesCrawled || 0,
        projectContactEmails: data.contact?.emails || [],
        projectContactPhones: data.contact?.phones || [],
        projectSourceUrl: data.url || lead.website,
        projectCrawledAt: new Date().toISOString(),
      });

      toast.success(`Project details extracted from ${data.pagesCrawled || 0} pages`, { id: "crawl-project" });
      return data;
    } catch (e) {
      toast.error("Project crawl failed: " + e.message, { id: "crawl-project" });
      return null;
    }
  };

  //  AI qualify 
  const [aiQualifying, setAiQualifying] = useState(false);
  const qualifyWithAI = async (lead) => {
    setAiQualifying(true);
    try {
      const result = await qualifyLeadWithAI({ groqKey: GROQ_KEY, groqChat, localQualify, lead });

      await updateLead(lead.id, {
        aiScore: result.score,
        aiReasons: result.reasons,
        aiProjectType: result.projectType,
        aiValue: result.estimatedValue,
        aiRecommendation: result.recommendation,
        aiSummary: result.summary,
        decision: result.recommendation === "do_it" ? "Do it" : result.recommendation === "refer_out" ? "Refer out" : "Undecided",
        projectValue: result.estimatedValue ? `${result.estimatedValue[0].toUpperCase()}${result.estimatedValue.slice(1)}` : undefined,
      });
      toast.success(GROQ_KEY ? "AI qualification complete" : "Qualification complete (local smart mode)");
    } catch { toast.error("AI qualification failed"); }
    setAiQualifying(false);
  };

  //  Generate outreach 
  const [outreachLoading, setOutreachLoading] = useState(false);
  const [outreachMsg, setOutreachMsg]         = useState("");
  const generateOutreach = async (lead, channel, tone = "Friendly") => {
    setOutreachLoading(true);
    try {
      const msg = await generateOutreachDraft({ groqKey: GROQ_KEY, groqChat, localOutreach, lead, channel, tone });
      setOutreachMsg(msg);
      await updateLead(lead.id, { outreachMsg: msg });
      toast.success("Outreach draft ready");
    } catch {
      const fallback = localOutreach(lead, channel, tone);
      setOutreachMsg(fallback);
      await updateLead(lead.id, { outreachMsg: fallback });
      toast.success("Outreach draft ready (fallback mode)");
    }
    setOutreachLoading(false);
  };

  // 
  //  RENDER
  // 
  if (legalPage === "privacy") return <PrivacyPolicy onBack={() => setLegalPage(null)} />;
  if (legalPage === "terms") return <TermsOfService onBack={() => setLegalPage(null)} />;
  if (showLanding) return <LandingPage onLaunch={() => setShowLanding(false)} />;
  if (!authToken) return <LoginPage onLogin={(token) => { setAuthToken(token); localStorage.setItem("lh_token", token); }} />;
  return (
    <div className="app">
      {/*  Header  */}
      <Header
        tab={tab}
        setTab={setTab}
        stats={stats}
        globalLoading={globalLoading}
        streamingCount={streamingCount}
        searchError={searchError}
        searchStatus={searchStatus}
        notificationsOpen={notificationsOpen}
        toggleNotifications={toggleNotifications}
        notificationsRef={notificationsRef}
        leadNotifications={leadNotifications}
        currentUser={currentUser}
        moreMenuOpen={moreMenuOpen}
        setMoreMenuOpen={setMoreMenuOpen}
        moreMenuRef={moreMenuRef}
        exportCSV={exportCSV}
        setConfirmModal={setConfirmModal}
        clearAllLeads={clearAllLeads}
        signOut={signOut}
      />

      <StatsBar
        stats={stats}
        searching={searching}
        goToPipeline={goToPipeline}
      />

      {/*  Main  */}
      <main className="main">
        {tab === "find"     && <FindTab sources={sources} setSources={setSources} industry={industry} setIndustry={setIndustry} city={city} setCity={setCity} keyword={keyword} setKeyword={setKeyword} cities={cities} oppCountries={oppCountries} setOppCountries={setOppCountries} oppLimit={oppLimit} setOppLimit={setOppLimit} oppRefineCountries={oppRefineCountries} setOppRefineCountries={setOppRefineCountries} opportunityCountryOptions={OPPORTUNITY_COUNTRY_OPTIONS} selectedSourceLabels={selectedSourceLabels} searching={searching} searchStatus={searchStatus} dbSaveStatus={dbSaveStatus} searchError={searchError} handleSearch={handleSearch} topCountryCounts={leadMeta.topCountries || {}} />}
        {tab === "paste"    && <PasteTab pasteText={pasteText} setPasteText={setPasteText} pasteLoading={pasteLoading} handlePaste={handlePaste} pasteSourceUrl={pasteSourceUrl} setPasteSourceUrl={setPasteSourceUrl} pasteSourceType={pasteSourceType} setPasteSourceType={setPasteSourceType} pastePostedAt={pastePostedAt} setPastePostedAt={setPastePostedAt} pasteLegalBasis={pasteLegalBasis} setPasteLegalBasis={setPasteLegalBasis} searching={searching} />}
        {tab === "pipeline" && <PipelineTab leads={leads} setTab={setTab} allLeadCount={pipelineTotal} hasMore={pipelineHasMore} isLoadingMore={pipelineLoading} onLoadMore={loadMorePipeline} selected={selected} setSelected={setSelected} pipeFilter={pipeFilter} setPipeFilter={setPipeFilter} pipeSource={pipeSource} setPipeSource={setPipeSource} pipeSearch={pipeSearch} setPipeSearch={setPipeSearch} pipeQuickFilter={pipeQuickFilter} setPipeQuickFilter={setPipeQuickFilter} viewMode={viewMode} setViewMode={setViewMode} uniqueSources={uniqueSources} sourceCounts={leadMeta.sourceCounts || {}} detailTab={detailTab} setDetailTab={setDetailTab} updateLead={updateLead} deleteLead={deleteLead} findEmail={findEmail} crawlEmail={crawlEmail} crawlProjectDetails={crawlProjectDetails} qualifyWithAI={qualifyWithAI} aiQualifying={aiQualifying} generateOutreach={generateOutreach} outreachLoading={outreachLoading} outreachMsg={outreachMsg} setOutreachMsg={setOutreachMsg} setConfirmModal={setConfirmModal} gmailStatus={gmailStatus} sendEmail={sendEmail} OutreachTabComponent={OutreachTab} searching={searching} />}
      </main>

      {/*  Toast (react-hot-toast)  */}
      <Toaster position="bottom-right" toastOptions={{
        duration: 3000,
        style: { background: "#1e293b", color: "#f1f5f9", borderRadius: "10px", fontSize: "14px", padding: "10px 16px" },
        success: { iconTheme: { primary: "#10b981", secondary: "#fff" } },
        error:   { iconTheme: { primary: "#ef4444", secondary: "#fff" } },
      }} />

      {/*  Tooltips  */}
      <Tooltip id="tt" style={{ fontSize: "12px", borderRadius: "8px", zIndex: 9999 }} />

      {showIdleWarning && (
        <div className="detail-modal-overlay">
          <div className="detail-modal-shell" style={{ maxWidth: 420, padding: 28, textAlign: "center" }}>
            <h3 style={{ margin: "0 0 12px", fontSize: 18 }}>Still there?</h3>
            <p style={{ margin: "0 0 20px", color: "#64748b", fontSize: 14 }}>
              You have been idle for a while. For security, you will be signed out in <strong>{idleSecondsLeft}s</strong>.
            </p>
            <button
              onClick={stayLoggedIn}
              style={{ background: "#4f46e5", color: "#fff", border: "none", borderRadius: 10, padding: "10px 24px", fontSize: 14, fontWeight: 600, cursor: "pointer" }}
            >
              Stay logged in
            </button>
          </div>
        </div>
      )}

      {/*  Legal Footer  */}
      <div style={{textAlign:"center",padding:"8px 0",borderTop:"1px solid #f1f5f9",fontSize:11,color:"#cbd5e1"}}>
        <button onClick={() => setLegalPage("privacy")} style={{background:"none",border:"none",color:"#94a3b8",cursor:"pointer",fontSize:11,textDecoration:"underline",marginRight:12}}>Privacy Policy</button>
        <button onClick={() => setLegalPage("terms")} style={{background:"none",border:"none",color:"#94a3b8",cursor:"pointer",fontSize:11,textDecoration:"underline"}}>Terms of Service</button>
        <span style={{marginLeft:12,color:"#cbd5e1"}}> 2026 WaveProspect</span>
      </div>

      {/*  Confirm Modal  */}
      {confirmModal && (
        <div className="modal-overlay" onClick={() => setConfirmModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title" style={{ pointerEvents: searching ? "none" : "auto" }}>Confirm</div>
            <div className="modal-msg" style={{ pointerEvents: searching ? "none" : "auto" }}>{confirmModal.message}</div>
            <div className="modal-actions" style={{ pointerEvents: searching ? "none" : "auto" }}>
              <button className="btn-ghost" onClick={() => setConfirmModal(null)}>Cancel</button>
              <button className="btn-danger" onClick={() => { confirmModal.onConfirm(); setConfirmModal(null); }}>Confirm</button>
            </div>
          </div>
        </div>
      )}
      {globalLoading && tab !== "pipeline" && leads.length === 0 && (
        <div className="loading-overlay" aria-live="polite" aria-busy="true">
          <div className="loader-box">
            <div className="spinner"></div>
            <p>{searchStatus || "Searching leads..."}</p>
          </div>
        </div>
      )}
    </div>
  );
}












